import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { onValue, ref, update as updateRealtimeData } from "firebase/database";
import { doc, updateDoc } from "firebase/firestore";
import { ArrowLeft, ChevronLeft, ChevronRight, LockKeyhole, Save } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { useAccess } from "../access/AccessProvider";
import { useAuth } from "../auth/AuthProvider";
import { db, realtimeDb } from "../firebase/config";
import { useTeams } from "../teams/TeamProvider";
function shortName(name) {
    const words = name.trim().split(/\s+/).filter((word) => !/^(boys?|girls?|men|women)$/i.test(word));
    if (words.length <= 1)
        return (words[0] ?? name).slice(0, 3).toUpperCase();
    return words.map((word) => word[0]).join("").slice(0, 4).toUpperCase();
}
function isRelay(name) {
    return /^4x/i.test(name.trim()) || /relay/i.test(name);
}
function addPoints(owner, points, total) {
    if (owner === 1)
        total.mine += points;
    if (owner === 2)
        total.opponent += points;
    if (owner === 0) {
        total.mine += points / 2;
        total.opponent += points / 2;
    }
}
function trackEventPoints(event) {
    const total = { mine: 0, opponent: 0 };
    if (event.firstPlace == null || event.firstPlace === -1)
        return total;
    if (isRelay(event.name ?? "")) {
        addPoints(event.firstPlace, 5, total);
        return total;
    }
    if (event.firstPlace === 0) {
        total.mine += 4;
        total.opponent += 4;
        addPoints(event.thirdPlace, 1, total);
        return total;
    }
    addPoints(event.firstPlace, 5, total);
    if (event.secondPlace === 0) {
        total.mine += 2;
        total.opponent += 2;
        return total;
    }
    addPoints(event.secondPlace, 3, total);
    addPoints(event.thirdPlace, 1, total);
    return total;
}
function formatScore(value) {
    return Number.isInteger(value) ? String(value) : value.toFixed(1);
}
function parsePlaces(value) {
    const places = (value ?? "").split("-").map(Number).filter(Number.isFinite).slice(0, 7);
    return [...places, ...Array(Math.max(0, 7 - places.length)).fill(0)];
}
function crossCountryTotal(places) {
    return places.filter((place) => place > 0).slice(0, 5).reduce((total, place) => total + place, 0);
}
function ordinal(value) {
    const remainder = value % 100;
    if (remainder >= 11 && remainder <= 13)
        return `${value}th`;
    if (value % 10 === 1)
        return `${value}st`;
    if (value % 10 === 2)
        return `${value}nd`;
    if (value % 10 === 3)
        return `${value}rd`;
    return `${value}th`;
}
export function DualMeetPage({ meet, onBack, publicContext }) {
    const { selectedTeam, selectedSeason } = useTeams();
    const team = publicContext?.team ?? selectedTeam;
    const season = publicContext?.season ?? selectedSeason;
    const { canWriteSeason, seasonAccess } = useAccess();
    const { hasSubscriptionAccess, subscriptionRequiredMessage } = useAuth();
    const opponents = useMemo(() => Object.entries(meet.opponent ?? {}).slice(0, 3), [meet.opponent]);
    const [opponentIndex, setOpponentIndex] = useState(0);
    const [trackScores, setTrackScores] = useState({});
    const [crossCountryScore, setCrossCountryScore] = useState({});
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [dirty, setDirty] = useState(false);
    const [saving, setSaving] = useState(false);
    const [openCell, setOpenCell] = useState(null);
    const [cellMenuPosition, setCellMenuPosition] = useState({ top: 0, left: 0, above: false });
    const opponentEntry = opponents[opponentIndex];
    const opponentKey = opponentEntry?.[0] ?? "opponent1";
    const opponentName = opponentEntry?.[1] ?? "Opponent";
    const crossCountry = season?.name.trim().toLowerCase() === "cross country" || Boolean(meet.course);
    const canWriteScore = !publicContext && canWriteSeason(season?.id);
    const canEdit = hasSubscriptionAccess && canWriteScore;
    const seasonLocked = !publicContext && seasonAccess(season?.id)?.locked === true;
    const subscriptionTitle = hasSubscriptionAccess ? undefined : subscriptionRequiredMessage;
    const teamShortName = shortName(team?.name ?? "Team");
    const opponentShortName = shortName(opponentName);
    useEffect(() => {
        if (!team || !season || !opponentEntry)
            return;
        setLoading(true);
        setError("");
        setDirty(false);
        return onValue(ref(realtimeDb, `competitions/${team.id}/${season.id}/scores/${meet.id}/${opponentKey}`), (snapshot) => {
            if (crossCountry)
                setCrossCountryScore(snapshot.val() ?? {});
            else
                setTrackScores(snapshot.val() ?? {});
            setLoading(false);
        }, (caughtError) => {
            setError(caughtError.message);
            setLoading(false);
        });
    }, [crossCountry, meet.id, opponentEntry, opponentKey, season, team]);
    useEffect(() => {
        const closeCellMenu = () => setOpenCell(null);
        document.addEventListener("click", closeCellMenu);
        return () => document.removeEventListener("click", closeCellMenu);
    }, []);
    const orderedTrackEvents = useMemo(() => Object.entries(trackScores)
        .map(([id, score]) => ({ id, ...score }))
        .sort((left, right) => Number(left.order ?? Number.MAX_SAFE_INTEGER) - Number(right.order ?? Number.MAX_SAFE_INTEGER) || (left.name ?? left.id).localeCompare(right.name ?? right.id)), [trackScores]);
    const trackTotals = useMemo(() => orderedTrackEvents.reduce((total, event) => {
        const points = trackEventPoints(event);
        total.mine += points.mine;
        total.opponent += points.opponent;
        return total;
    }, { mine: 0, opponent: 0 }), [orderedTrackEvents]);
    const myPlaces = parsePlaces(crossCountryScore.myPlaces);
    const opponentPlaces = parsePlaces(crossCountryScore.opponentPlaces);
    const totals = crossCountry ? { mine: crossCountryTotal(myPlaces), opponent: crossCountryTotal(opponentPlaces) } : trackTotals;
    async function setTrackPlace(eventId, place, owner) {
        if (!team || !season || !canEdit)
            return;
        const event = trackScores[eventId] ?? {};
        const updates = { [place]: owner };
        if (place === "firstPlace" && owner === 0) {
            updates.secondPlace = 0;
        }
        else if (place === "secondPlace" && owner === 0) {
            updates.thirdPlace = 0;
        }
        else if (place === "firstPlace" && event.secondPlace === 0) {
            updates.secondPlace = -1;
        }
        else if (place === "secondPlace" && event.thirdPlace === 0) {
            updates.thirdPlace = -1;
        }
        setDirty(true);
        await updateRealtimeData(ref(realtimeDb, `competitions/${team.id}/${season.id}/scores/${meet.id}/${opponentKey}/${eventId}`), updates);
    }
    async function setCrossCountryPlace(field, index, value) {
        if (!team || !season || !canEdit)
            return;
        const places = field === "myPlaces" ? [...myPlaces] : [...opponentPlaces];
        places[index] = value;
        setDirty(true);
        await updateRealtimeData(ref(realtimeDb, `competitions/${team.id}/${season.id}/scores/${meet.id}/${opponentKey}`), { [field]: places.join("-") });
    }
    async function saveScore() {
        if (!dirty)
            return true;
        if (!team || !canEdit)
            return false;
        setSaving(true);
        setError("");
        try {
            await updateDoc(doc(db, "teams", team.id, "schedule", meet.id), {
                [`score.${opponentKey}`]: `${formatScore(totals.mine)} - ${formatScore(totals.opponent)}`,
            });
            setDirty(false);
            return true;
        }
        catch (caughtError) {
            setError(caughtError instanceof Error ? caughtError.message : "The score could not be saved.");
            return false;
        }
        finally {
            setSaving(false);
        }
    }
    async function goBack() {
        if (await saveScore())
            onBack();
    }
    async function changeOpponent(nextIndex) {
        if (await saveScore())
            setOpponentIndex(nextIndex);
    }
    return _jsxs("div", { className: `content dual-score-page${canWriteScore ? "" : " dual-score-page--readonly"}`, children: [_jsxs("button", { className: "back-button", type: "button", onClick: () => void goBack(), children: [_jsx(ArrowLeft, { size: 17 }), " Back to meet"] }), _jsxs("section", { className: "dual-score-hero", children: [_jsxs("div", { children: [_jsx("span", { children: "DUAL MEET SCORE" }), _jsx("h1", { children: meet.name })] }), _jsxs("div", { className: "dual-score-hero__score", children: [_jsx("strong", { children: formatScore(totals.mine) }), _jsx("span", { children: "\u2013" }), _jsx("strong", { children: formatScore(totals.opponent) })] }), canWriteScore && _jsxs("button", { type: "button", disabled: !hasSubscriptionAccess || !dirty || saving, title: subscriptionTitle, onClick: () => void saveScore(), children: [_jsx(Save, { size: 16 }), saving ? "Saving…" : dirty ? "Save score" : "Saved"] })] }), seasonLocked && _jsxs("div", { className: "season-locked-notice", role: "status", children: [_jsx(LockKeyhole, { size: 18 }), _jsxs("div", { children: [_jsx("strong", { children: "Season locked" }), _jsx("span", { children: "This season is completed. Meet scores are view-only and cannot be edited." })] })] }), opponents.length > 1 && _jsxs("nav", { className: "opponent-switcher", "aria-label": "Select opponent", children: [_jsx("button", { type: "button", disabled: opponentIndex === 0 || saving, onClick: () => void changeOpponent(opponentIndex - 1), children: _jsx(ChevronLeft, { size: 17 }) }), _jsxs("div", { children: [_jsxs("small", { children: ["OPPONENT ", opponentIndex + 1, " OF ", opponents.length] }), _jsx("strong", { children: opponentName })] }), _jsx("button", { type: "button", disabled: opponentIndex === opponents.length - 1 || saving, onClick: () => void changeOpponent(opponentIndex + 1), children: _jsx(ChevronRight, { size: 17 }) })] }), loading && _jsx("div", { className: "details-state", children: _jsx("strong", { children: "Loading score\u2026" }) }), !loading && error && _jsxs("div", { className: "details-state details-state--error", children: [_jsx("strong", { children: "Score could not be loaded" }), _jsx("span", { children: "Access denied" })] }), !loading && !error && crossCountry && _jsxs("section", { className: "xc-score-card panel", children: [_jsxs("header", { children: [_jsx("strong", { children: selectedTeam?.name ?? "My Team" }), _jsx("strong", { children: opponentName })] }), _jsxs("div", { className: "xc-place-columns", children: [_jsx("div", { children: myPlaces.map((place, index) => _jsx("label", { children: _jsxs("select", { "aria-label": `${selectedTeam?.name} runner ${index + 1} place`, value: place, disabled: !canEdit, title: !hasSubscriptionAccess ? subscriptionTitle : undefined, onChange: (event) => void setCrossCountryPlace("myPlaces", index, Number(event.target.value)), children: [_jsx("option", { value: 0, children: "-" }), Array.from({ length: 14 }, (_, value) => value + 1).map((value) => _jsx("option", { value: value, children: ordinal(value) }, value))] }) }, index)) }), _jsx("div", { children: opponentPlaces.map((place, index) => _jsx("label", { children: _jsxs("select", { "aria-label": `${opponentName} runner ${index + 1} place`, value: place, disabled: !canEdit, title: !hasSubscriptionAccess ? subscriptionTitle : undefined, onChange: (event) => void setCrossCountryPlace("opponentPlaces", index, Number(event.target.value)), children: [_jsx("option", { value: 0, children: "-" }), Array.from({ length: 14 }, (_, value) => value + 1).map((value) => _jsx("option", { value: value, children: ordinal(value) }, value))] }) }, index)) })] }), _jsxs("footer", { children: [_jsx("strong", { children: totals.mine }), _jsx("strong", { children: totals.opponent })] })] }), !loading && !error && !crossCountry && _jsx("section", { className: "track-score-card panel", children: _jsxs("div", { className: "track-score-table", children: [_jsxs("header", { children: [_jsx("strong", { children: "Event" }), _jsx("strong", { children: "1st" }), _jsx("strong", { children: "2nd" }), _jsx("strong", { children: "3rd" }), _jsx("strong", { children: teamShortName }), _jsx("strong", { children: opponentShortName })] }), orderedTrackEvents.map((event) => {
                            const points = trackEventPoints(event);
                            const relay = isRelay(event.name ?? "");
                            const secondLocked = relay || event.firstPlace === 0;
                            const thirdLocked = relay || (event.secondPlace === 0 && event.firstPlace !== 0);
                            const placeSelect = (place, value, locked = false) => {
                                const cellId = `${event.id}-${place}`;
                                const displayValue = value === 1 ? teamShortName : value === 2 ? opponentShortName : value === 0 ? "Tie" : "—";
                                return _jsxs("div", { className: "score-choice", onClick: (clickEvent) => clickEvent.stopPropagation(), children: [_jsx("button", { type: "button", disabled: !canEdit || locked, title: !hasSubscriptionAccess ? subscriptionTitle : undefined, onClick: (clickEvent) => { const rect = clickEvent.currentTarget.getBoundingClientRect(); const above = rect.bottom + 170 > window.innerHeight; setCellMenuPosition({ top: above ? rect.top - 4 : rect.bottom + 4, left: rect.left + rect.width / 2, above }); setOpenCell((current) => current === cellId ? null : cellId); }, children: displayValue }), openCell === cellId && createPortal(_jsxs("div", { className: `score-choice__menu score-choice__menu--portal ${cellMenuPosition.above ? "score-choice__menu--above" : ""}`, style: { top: cellMenuPosition.top, left: cellMenuPosition.left }, onClick: (clickEvent) => clickEvent.stopPropagation(), children: [_jsx("button", { type: "button", onClick: () => { setOpenCell(null); void setTrackPlace(event.id, place, 1); }, children: selectedTeam?.name ?? "My team" }), _jsx("button", { type: "button", onClick: () => { setOpenCell(null); void setTrackPlace(event.id, place, 2); }, children: opponentName }), _jsx("button", { type: "button", onClick: () => { setOpenCell(null); void setTrackPlace(event.id, place, 0); }, children: "Tie" }), _jsx("button", { type: "button", onClick: () => { setOpenCell(null); void setTrackPlace(event.id, place, -1); }, children: "No Score" })] }), document.body)] });
                            };
                            return _jsxs("div", { className: "track-score-row", children: [_jsx("strong", { children: event.name ?? event.id }), placeSelect("firstPlace", event.firstPlace), placeSelect("secondPlace", event.secondPlace, secondLocked), placeSelect("thirdPlace", event.thirdPlace, thirdLocked), _jsx("span", { children: formatScore(points.mine) }), _jsx("span", { children: formatScore(points.opponent) })] }, event.id);
                        }), _jsxs("footer", { children: [_jsx("strong", { children: "Total" }), _jsx("strong", { children: formatScore(totals.mine) }), _jsx("strong", { children: formatScore(totals.opponent) })] })] }) })] });
}
